import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {SchemaPropertyType} from "@vesta/vos-types/modules/platformLevel/storageDatabases/domain/StorageDatabaseSchema";
import {z as zod} from "zod";

/** Shared storage and semantic column types for UDOs and capability-owned templates. */
export enum UserDefinedObjectTemplateAdditionalColumnType {
    boolean = "e7d02c3a-aa6a-4008-88c4-9bc20254b7c8",
    email = "fb177ceb-53fd-471e-80b5-0b589b08406b",
    geographyCountries = "2ef262ac-acff-4eb9-98c2-41714e9a6e86",
    geographyIndiaCities = "1b7361a4-9855-46a6-803e-8aab493004ad",
    geographyIndiaPincodes = "42bedc07-7ff7-489b-91bf-8daabcc6daf3",
    geographyIndiaStates = "763daa0c-1bcd-48b7-9afd-0c11ce5f5907",
    geographyIndiaZones = "d89e682a-5685-42d8-9d7f-67cce79d1406",
    geographyLatitude = "0456c669-2029-44fa-a632-ff856df574ee",
    geographyLongitude = "a2a4a2d2-50a7-4539-8592-897be15639c3",
    image = "06a30d51-dcf6-4165-824d-9c9a9814a76c",
    indianPhoneNumber = "37766376-0fcc-4efc-85d8-a95c592c8690",
    integer = "13f0ce00-f672-4065-b0d1-fd0813da6146",
    json = "a90ec580-99ec-404f-9b71-b1190ad535e9",
    number = "64491d77-8741-4b95-9d6c-38e4e260192c",
    ou = "38e38250-dcdf-42de-bcc9-0e25e3fcefb7",
    ous = "ed89c67a-e834-4970-affe-a5903a351a03",
    product = "c909093e-12f4-4f6e-b4af-157b0c8440c9",
    string = "d989bbb6-3611-489a-846d-653b15b6dad2",
    timestamp = "752dd476-a551-4c34-9ad3-a801bd07f3aa",
    user = "f2a3c045-d08a-446d-9c73-e238f28089c0",
    userDefinedObject = "b832db23-b0b6-416b-9e9c-9b48a3dcde17",
    userDefinedObjects = "b364bc29-c7f2-4660-a7cd-47169001b2b3",
    userTemplate = "c91911f5-dc6a-4006-a071-f5b528e44b3e",
    uuid = "33cf4ee9-c952-44b2-b536-24c9ed6a2b14",
}

export enum UserDefinedObjectTemplateAdditionalColumnSourceType {
    physical = "01295bd4-0d0e-4058-8477-dfd9fb8cffd2",
    resolver = "e04e0ef7-4210-4c3e-aff5-3fc48d342964",
}

export type UserDefinedObjectTemplateAdditionalColumnSource =
    | {
          required: boolean;
          type: UserDefinedObjectTemplateAdditionalColumnSourceType.physical;
      }
    | {
          sql: string;
          type: UserDefinedObjectTemplateAdditionalColumnSourceType.resolver;
      };

type UserDefinedObjectTemplateAdditionalColumnBase = {
    displayName: string;
    id: Uuid;
    source: UserDefinedObjectTemplateAdditionalColumnSource;
};

export type UserDefinedObjectTemplateAdditionalColumn = UserDefinedObjectTemplateAdditionalColumnBase & (
    | {
          type:
              | UserDefinedObjectTemplateAdditionalColumnType.boolean
              | UserDefinedObjectTemplateAdditionalColumnType.email
              | UserDefinedObjectTemplateAdditionalColumnType.geographyCountries
              | UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaCities
              | UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaPincodes
              | UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaStates
              | UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaZones
              | UserDefinedObjectTemplateAdditionalColumnType.geographyLatitude
              | UserDefinedObjectTemplateAdditionalColumnType.geographyLongitude
              | UserDefinedObjectTemplateAdditionalColumnType.image
              | UserDefinedObjectTemplateAdditionalColumnType.indianPhoneNumber
              | UserDefinedObjectTemplateAdditionalColumnType.integer
              | UserDefinedObjectTemplateAdditionalColumnType.number
              | UserDefinedObjectTemplateAdditionalColumnType.product
              | UserDefinedObjectTemplateAdditionalColumnType.string
              | UserDefinedObjectTemplateAdditionalColumnType.timestamp
              | UserDefinedObjectTemplateAdditionalColumnType.user
              | UserDefinedObjectTemplateAdditionalColumnType.userTemplate
              | UserDefinedObjectTemplateAdditionalColumnType.uuid;
      }
    | {
          type: UserDefinedObjectTemplateAdditionalColumnType.json | UserDefinedObjectTemplateAdditionalColumnType.ou;
          configuration?: {ouTemplateId?: Uuid};
      }
    | {
          type: UserDefinedObjectTemplateAdditionalColumnType.ous;
          configuration: {ouTemplateId: Uuid};
      }
    | {
          type: UserDefinedObjectTemplateAdditionalColumnType.userDefinedObject | UserDefinedObjectTemplateAdditionalColumnType.userDefinedObjects;
          configuration: {userDefinedObjectTemplateId: Uuid};
      }
);

export type UserDefinedObjectTemplateAdditionalColumnValueTypeMap = {
    [UserDefinedObjectTemplateAdditionalColumnType.boolean]: boolean;
    [UserDefinedObjectTemplateAdditionalColumnType.email]: string;
    [UserDefinedObjectTemplateAdditionalColumnType.geographyCountries]: Uuid;
    [UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaCities]: Uuid;
    [UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaPincodes]: Uuid;
    [UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaStates]: Uuid;
    [UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaZones]: Uuid;
    [UserDefinedObjectTemplateAdditionalColumnType.geographyLatitude]: number;
    [UserDefinedObjectTemplateAdditionalColumnType.geographyLongitude]: number;
    [UserDefinedObjectTemplateAdditionalColumnType.image]: string;
    [UserDefinedObjectTemplateAdditionalColumnType.indianPhoneNumber]: string;
    [UserDefinedObjectTemplateAdditionalColumnType.integer]: number;
    [UserDefinedObjectTemplateAdditionalColumnType.json]: unknown;
    [UserDefinedObjectTemplateAdditionalColumnType.number]: number;
    [UserDefinedObjectTemplateAdditionalColumnType.ou]: Uuid;
    [UserDefinedObjectTemplateAdditionalColumnType.ous]: ReadonlyArray<Uuid>;
    [UserDefinedObjectTemplateAdditionalColumnType.product]: Uuid;
    [UserDefinedObjectTemplateAdditionalColumnType.string]: string;
    [UserDefinedObjectTemplateAdditionalColumnType.timestamp]: Iso8601DateTime;
    [UserDefinedObjectTemplateAdditionalColumnType.user]: Uuid;
    [UserDefinedObjectTemplateAdditionalColumnType.userDefinedObject]: Uuid;
    [UserDefinedObjectTemplateAdditionalColumnType.userDefinedObjects]: ReadonlyArray<Uuid>;
    [UserDefinedObjectTemplateAdditionalColumnType.userTemplate]: Uuid;
    [UserDefinedObjectTemplateAdditionalColumnType.uuid]: Uuid;
};

export type UserDefinedObjectTemplateAdditionalColumnValue<Column extends UserDefinedObjectTemplateAdditionalColumn> =
    UserDefinedObjectTemplateAdditionalColumnValueTypeMap[Column["type"]]
    | (Column["source"] extends {required: true; type: UserDefinedObjectTemplateAdditionalColumnSourceType.physical} ? never : null);

export const userDefinedObjectTemplateAdditionalColumnSourceValidator = zod.discriminatedUnion("type", [
    zod.strictObject({required: zod.boolean(), type: zod.literal(UserDefinedObjectTemplateAdditionalColumnSourceType.physical)}),
    zod.strictObject({sql: zod.string().trim().min(1), type: zod.literal(UserDefinedObjectTemplateAdditionalColumnSourceType.resolver)}),
]) satisfies zod.ZodType<UserDefinedObjectTemplateAdditionalColumnSource>;

const additionalColumnBaseValidator = zod.strictObject({
    displayName: zod.string().trim().min(1),
    id: uuidZodValidator,
    source: userDefinedObjectTemplateAdditionalColumnSourceValidator,
});

export const userDefinedObjectTemplateAdditionalColumnValidator = zod.discriminatedUnion("type", [
    additionalColumnBaseValidator.extend({
        type: zod.enum([
            UserDefinedObjectTemplateAdditionalColumnType.boolean,
            UserDefinedObjectTemplateAdditionalColumnType.email,
            UserDefinedObjectTemplateAdditionalColumnType.geographyCountries,
            UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaCities,
            UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaPincodes,
            UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaStates,
            UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaZones,
            UserDefinedObjectTemplateAdditionalColumnType.geographyLatitude,
            UserDefinedObjectTemplateAdditionalColumnType.geographyLongitude,
            UserDefinedObjectTemplateAdditionalColumnType.image,
            UserDefinedObjectTemplateAdditionalColumnType.indianPhoneNumber,
            UserDefinedObjectTemplateAdditionalColumnType.integer,
            UserDefinedObjectTemplateAdditionalColumnType.number,
            UserDefinedObjectTemplateAdditionalColumnType.product,
            UserDefinedObjectTemplateAdditionalColumnType.string,
            UserDefinedObjectTemplateAdditionalColumnType.timestamp,
            UserDefinedObjectTemplateAdditionalColumnType.user,
            UserDefinedObjectTemplateAdditionalColumnType.userTemplate,
            UserDefinedObjectTemplateAdditionalColumnType.uuid,
        ]),
    }),
    additionalColumnBaseValidator.extend({
        type: zod.literal(UserDefinedObjectTemplateAdditionalColumnType.json),
        configuration: zod.strictObject({ouTemplateId: uuidZodValidator.optional()}).optional(),
    }),
    additionalColumnBaseValidator.extend({
        type: zod.literal(UserDefinedObjectTemplateAdditionalColumnType.ou),
        configuration: zod.strictObject({ouTemplateId: uuidZodValidator.optional()}).optional(),
    }),
    additionalColumnBaseValidator.extend({
        type: zod.literal(UserDefinedObjectTemplateAdditionalColumnType.ous),
        configuration: zod.strictObject({ouTemplateId: uuidZodValidator}),
    }),
    additionalColumnBaseValidator.extend({
        type: zod.literal(UserDefinedObjectTemplateAdditionalColumnType.userDefinedObject),
        configuration: zod.strictObject({userDefinedObjectTemplateId: uuidZodValidator}),
    }),
    additionalColumnBaseValidator.extend({
        type: zod.literal(UserDefinedObjectTemplateAdditionalColumnType.userDefinedObjects),
        configuration: zod.strictObject({userDefinedObjectTemplateId: uuidZodValidator}),
    }),
]) satisfies zod.ZodType<UserDefinedObjectTemplateAdditionalColumn>;

export const userDefinedObjectTemplateAdditionalColumnsValidator = userDefinedObjectTemplateAdditionalColumnValidator.array().superRefine((columns, context) => {
    const seenIds = new Set<Uuid>();
    for (const [index, column] of columns.entries()) {
        if (seenIds.has(column.id)) {
            context.addIssue({code: "custom", message: `Duplicate additional column id: ${column.id}`, path: [index, "id"]});
        }
        seenIds.add(column.id);
    }
});

/** Value shape only. Reference existence and authorization belong to the consuming services. */
export const userDefinedObjectTemplateAdditionalColumnValueValidatorMap = {
    [UserDefinedObjectTemplateAdditionalColumnType.boolean]: zod.boolean(),
    [UserDefinedObjectTemplateAdditionalColumnType.email]: zod.string(),
    [UserDefinedObjectTemplateAdditionalColumnType.geographyCountries]: uuidZodValidator,
    [UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaCities]: uuidZodValidator,
    [UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaPincodes]: uuidZodValidator,
    [UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaStates]: uuidZodValidator,
    [UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaZones]: uuidZodValidator,
    [UserDefinedObjectTemplateAdditionalColumnType.geographyLatitude]: zod.number(),
    [UserDefinedObjectTemplateAdditionalColumnType.geographyLongitude]: zod.number(),
    [UserDefinedObjectTemplateAdditionalColumnType.image]: zod.string(),
    [UserDefinedObjectTemplateAdditionalColumnType.indianPhoneNumber]: zod.string(),
    [UserDefinedObjectTemplateAdditionalColumnType.integer]: zod.number().int(),
    [UserDefinedObjectTemplateAdditionalColumnType.json]: zod.unknown(),
    [UserDefinedObjectTemplateAdditionalColumnType.number]: zod.number(),
    [UserDefinedObjectTemplateAdditionalColumnType.ou]: uuidZodValidator,
    [UserDefinedObjectTemplateAdditionalColumnType.ous]: uuidZodValidator.array(),
    [UserDefinedObjectTemplateAdditionalColumnType.product]: uuidZodValidator,
    [UserDefinedObjectTemplateAdditionalColumnType.string]: zod.string(),
    [UserDefinedObjectTemplateAdditionalColumnType.timestamp]: iso8601DateTimeZodValidator,
    [UserDefinedObjectTemplateAdditionalColumnType.user]: uuidZodValidator,
    [UserDefinedObjectTemplateAdditionalColumnType.userDefinedObject]: uuidZodValidator,
    [UserDefinedObjectTemplateAdditionalColumnType.userDefinedObjects]: uuidZodValidator.array(),
    [UserDefinedObjectTemplateAdditionalColumnType.userTemplate]: uuidZodValidator,
    [UserDefinedObjectTemplateAdditionalColumnType.uuid]: uuidZodValidator,
} satisfies Record<UserDefinedObjectTemplateAdditionalColumnType, zod.ZodType>;

export const userDefinedObjectTemplateAdditionalColumnSchemaTypeMap = {
    [UserDefinedObjectTemplateAdditionalColumnType.boolean]: SchemaPropertyType.boolean,
    [UserDefinedObjectTemplateAdditionalColumnType.email]: SchemaPropertyType.string,
    [UserDefinedObjectTemplateAdditionalColumnType.geographyCountries]: SchemaPropertyType.uuid,
    [UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaCities]: SchemaPropertyType.uuid,
    [UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaPincodes]: SchemaPropertyType.uuid,
    [UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaStates]: SchemaPropertyType.uuid,
    [UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaZones]: SchemaPropertyType.uuid,
    [UserDefinedObjectTemplateAdditionalColumnType.geographyLatitude]: SchemaPropertyType.number,
    [UserDefinedObjectTemplateAdditionalColumnType.geographyLongitude]: SchemaPropertyType.number,
    [UserDefinedObjectTemplateAdditionalColumnType.image]: SchemaPropertyType.string,
    [UserDefinedObjectTemplateAdditionalColumnType.indianPhoneNumber]: SchemaPropertyType.string,
    [UserDefinedObjectTemplateAdditionalColumnType.integer]: SchemaPropertyType.integer,
    [UserDefinedObjectTemplateAdditionalColumnType.json]: SchemaPropertyType.json,
    [UserDefinedObjectTemplateAdditionalColumnType.number]: SchemaPropertyType.number,
    [UserDefinedObjectTemplateAdditionalColumnType.ou]: SchemaPropertyType.uuid,
    [UserDefinedObjectTemplateAdditionalColumnType.ous]: SchemaPropertyType.json,
    [UserDefinedObjectTemplateAdditionalColumnType.product]: SchemaPropertyType.uuid,
    [UserDefinedObjectTemplateAdditionalColumnType.string]: SchemaPropertyType.string,
    [UserDefinedObjectTemplateAdditionalColumnType.timestamp]: SchemaPropertyType.timestamp,
    [UserDefinedObjectTemplateAdditionalColumnType.user]: SchemaPropertyType.uuid,
    [UserDefinedObjectTemplateAdditionalColumnType.userDefinedObject]: SchemaPropertyType.uuid,
    [UserDefinedObjectTemplateAdditionalColumnType.userDefinedObjects]: SchemaPropertyType.json,
    [UserDefinedObjectTemplateAdditionalColumnType.userTemplate]: SchemaPropertyType.uuid,
    [UserDefinedObjectTemplateAdditionalColumnType.uuid]: SchemaPropertyType.uuid,
} satisfies Record<UserDefinedObjectTemplateAdditionalColumnType, SchemaPropertyType>;

export function getSchemaPropertyTypeFromUserDefinedObjectTemplateAdditionalColumnType(type: UserDefinedObjectTemplateAdditionalColumnType): SchemaPropertyType {
    return userDefinedObjectTemplateAdditionalColumnSchemaTypeMap[type];
}
